import some from 'lodash/some';
import { useSearchParams } from 'react-router-dom';
import { match } from 'ts-pattern';

import { Button, FCC, Heading, Stack, tv } from '@eluve/components';
import { urlSafeDate } from '@eluve/date-utils';
import { useAppointmentCounts } from '@eluve/frontend-appointment-hooks';
import { useUserLocations } from '@eluve/frontend-feature-location-hierarchy';
import { isVendorWebBased } from '@eluve/graphql-types';
import { useTranslation } from '@eluve/i18n';
import { useCaptureEventOnMount } from '@eluve/posthog-react';
import { useTenantIdFromParams } from '@eluve/session-helpers';
import { useEluveExtExists } from '@eluve/smart-blocks';
import { SUPPORT_URL_TROUBLESHOOTING_EHR_IMPORTS } from '@eluve/utils';
import { getVendorDataMapper } from '@eluve/vendor';

import { appConfig } from '../../../../../config';
import { CHROME_WEB_STORE_URL } from '../constants';
import { useStartAnonymousSession } from '../hooks/useStartAnonymousSession';
import { useEhrStatusDetails } from '../useEhrStatusDetails';
import { useImportDataFromEhr } from '../useImportDataFromEhr';

const EmptyStateBox: FCC = ({ children }) => {
  const { t } = useTranslation();
  return (
    <Stack align="center" justify="center" fullHeight className="p-2">
      <Stack
        className="max-w-[540px] rounded-lg border bg-bg-primary p-6 shadow-lg"
        gap={2}
        align="center"
      >
        <Heading size="lg" level={3} className="font-normal sm:text-2xl">
          {t('home.emptyState.noUpcomingAppointments')}
        </Heading>
        {children}
      </Stack>
    </Stack>
  );
};

const emptyStateDescription = tv({
  base: 'p-3 text-center text-sm text-text-tertiary',
});

const PromptToFirstTimeImport: React.FC = () => {
  const { t } = useTranslation();
  const [_searchParams, setSearchParams] = useSearchParams();
  const { startSession, isPending } = useStartAnonymousSession();

  useCaptureEventOnMount('empty_home_state_prompt_to_first_time_import_shown');

  return (
    <EmptyStateBox>
      <span className={emptyStateDescription()}>
        {t('home.emptyState.finishSetupDescription')}
      </span>
      <Stack justify="center" gap={2} direction="horizontal">
        <Button
          variant="outline"
          onClick={() => {
            setSearchParams({ event: 'onboarding' });
          }}
        >
          {t('home.emptyState.importAppointments')}
        </Button>
        <Button variant="outline" onClick={startSession} loading={isPending}>
          {t('home.emptyState.newSession')}
        </Button>
      </Stack>
    </EmptyStateBox>
  );
};

const PromptToRefresh: React.FC = () => {
  const { t } = useTranslation();
  const [_searchParams] = useSearchParams();
  const tenantId = useTenantIdFromParams();
  const { importDataFromEhr, isDataImporting } = useImportDataFromEhr(
    tenantId!,
  );

  useCaptureEventOnMount('empty_home_state_prompt_to_refresh_shown');

  return (
    <EmptyStateBox>
      <span className={emptyStateDescription()}>
        {t('home.emptyState.noUpcomingNext7Days')}
      </span>
      <Button
        variant="outline"
        className="m-1 w-full max-w-96"
        disabled={isDataImporting}
        onClick={() => {
          importDataFromEhr();
        }}
      >
        {t('home.emptyState.refresh')}
      </Button>
      <Button
        variant="link"
        className="m-1 text-text-tertiary underline"
        onClick={() => {
          window.open(
            SUPPORT_URL_TROUBLESHOOTING_EHR_IMPORTS,
            '_blank',
            'noopener,noreferrer',
          );
        }}
      >
        {t('home.emptyState.troubleshoot')}
      </Button>
    </EmptyStateBox>
  );
};

const PromptToInstallExtension: React.FC = () => {
  const { t } = useTranslation();
  const { startSession, isPending } = useStartAnonymousSession();

  useCaptureEventOnMount('empty_home_state_prompt_to_install_shown');

  return (
    <EmptyStateBox>
      <span className={emptyStateDescription()}>
        {t('home.emptyState.downloadExtensionDescription')}
      </span>
      <Stack direction="horizontal" fullWidth={false} gap={2}>
        <Button
          variant="outline"
          className="m-1"
          onClick={() => {
            window.open(CHROME_WEB_STORE_URL, '_blank', 'noopener,noreferrer');
          }}
        >
          {t('home.emptyState.installExtension')}
        </Button>
        <Button
          variant="outline"
          onClick={startSession}
          loading={isPending}
          aria-label={t('home.emptyState.startRecordingSession')}
        >
          {t('home.emptyState.newSession')}
        </Button>
      </Stack>
    </EmptyStateBox>
  );
};

export const EmptyHomePageState: React.FC = () => {
  const tenantId = useTenantIdFromParams();
  const { eluveExtExists } = useEluveExtExists();
  const [searchParams] = useSearchParams();
  const { data: ehrStatus } = useEhrStatusDetails();
  const { isSupportedBrowser } = useImportDataFromEhr(tenantId!);
  const fromDate = searchParams.get('date') ?? urlSafeDate();

  const { isUserPartOfAnyLocation, locationUserExternalEhrs } =
    useUserLocations();
  const isUserCurrentlyOnboarding = searchParams.get('event') === 'onboarding';

  const currentTenantEhrStatus =
    ehrStatus?.eluveDomains?.[appConfig.VITE_API_DOMAIN]?.tenants?.[tenantId!];
  const hasSyncedBefore = some(currentTenantEhrStatus?.vendors, (vendor) =>
    some(vendor?.domains, (domain) => Boolean(domain.syncedAt)),
  );

  const didUserFinishOnboarding = isUserPartOfAnyLocation && hasSyncedBefore;
  const shouldPromptUserToFinishOnboarding =
    !didUserFinishOnboarding && !isUserCurrentlyOnboarding;

  const { totalAppointmentsCount, upcomingAppointmentsCount } =
    useAppointmentCounts({ days: 7, fromDate });

  const externalEhrs = locationUserExternalEhrs.filter(
    (ehr) => getVendorDataMapper(ehr.vendor).getSyncConfig().isImportEnabled,
  );

  const isExtensionRequired = externalEhrs.some((ehr) =>
    isVendorWebBased(ehr.vendor),
  );

  return match({
    isSupportedBrowser,
    eluveExtExists,
    isExtensionRequired,
    shouldPromptUserToFinishOnboarding,
    upcomingAppointmentsCount,
    totalAppointmentsCount,
  })
    .with(
      {
        isExtensionRequired: true,
        eluveExtExists: false,
        isSupportedBrowser: true,
      },
      () => <PromptToInstallExtension />,
    )
    .with(
      {
        isExtensionRequired: true,
        eluveExtExists: true,
        shouldPromptUserToFinishOnboarding: true,
      },
      () => <PromptToFirstTimeImport />,
    )
    .with(
      {
        isExtensionRequired: true,
        eluveExtExists: true,
        upcomingAppointmentsCount: 0,
        shouldPromptUserToFinishOnboarding: false,
      },
      () => <PromptToRefresh />,
    )
    .otherwise(() => null);
};
